import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTrip } from '../../contexts/TripContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Plane, Car, Bus, Truck, Train, Bike, Ship, CircleDot,
  MapPin, Calendar, Users, Building2, UserPlus, Edit2, Trash2,
  CheckCircle2, Clock, Crown, ArrowRight, ArrowLeft,
  ChevronRight, Loader2, CreditCard, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

import { TRANSPORT_TYPES, RELATIONSHIP_OPTIONS, DEPENDENT_STATUS } from './constants';
import WelcomeModal from './WelcomeModal';
import TransportDetailsModal from './TransportDetailsModal';
import AccommodationModal from './AccommodationModal';
import DependentModal from './DependentModal';
import ExplanationModal from './ExplanationModal';
import TransportCostModal from './TransportCostModal';

const iconMap = {
  Plane, Car, Bus, Truck, Train, Bike, Ship, CircleDot
};

// Helper for async delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const TripMyTrip = () => {
  const { tripId } = useParams();
  const { currentTrip: trip, members } = useTrip();
  const { user, api } = useAuth();
  const { t, language } = useLanguage();
  const locale = language === 'pt-BR' ? ptBR : enUS;

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [memberInfo, setMemberInfo] = useState(null);
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showTransportModal, setShowTransportModal] = useState(false);
  const [showAccommodationModal, setShowAccommodationModal] = useState(false);
  const [showDependentModal, setShowDependentModal] = useState(false);
  const [showTransportCostModal, setShowTransportCostModal] = useState(false);
  const [editingDependent, setEditingDependent] = useState(null);
  const [hasSeenExplanation, setHasSeenExplanation] = useState(false);
  
  // Transport editing state
  const [editingTransportDirection, setEditingTransportDirection] = useState(null); // 'outbound' or 'return'
  const [pendingTransportCostDirection, setPendingTransportCostDirection] = useState(null);
  const [showEditWarning, setShowEditWarning] = useState(false);

  const isOrganizer = trip?.organizer_id === user?.id;
  
  // Check if transport is shared from trip settings
  const isSharedTransport = trip?.settings?.shared_transport === true;
  const tripCurrency = trip?.settings?.currency || 'BRL';

  // Get effective transport data (from shared settings or member's own info)
  const getEffectiveTransport = () => {
    if (isSharedTransport && trip?.settings) {
      return {
        outbound: trip.settings.outbound_transport_type,
        return: trip.settings.return_transport_type,
        cost: trip.settings.transport_cost,
        installments: trip.settings.transport_installments,
      };
    }
    return {
      outbound: memberInfo?.outbound_transport_type || memberInfo?.transport_type,
      return: memberInfo?.return_transport_type || memberInfo?.transport_type,
      cost: null,
      installments: null,
    };
  };

  const effectiveTransport = getEffectiveTransport();

  // Fetch member info
  const fetchMemberInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trips/${tripId}/member-info/me`);
      setMemberInfo(response.data);
      
      // If not completed and hasn't seen explanation yet, show explanation modal
      if (!response.data.is_completed && response.data.is_traveling !== false) {
        const seenKey = `mytrip_explanation_${tripId}_${user?.id}`;
        const hasSeen = localStorage.getItem(seenKey);
        
        if (!hasSeen && !response.data.outbound_transport_type && !response.data.transport_type) {
          setShowExplanationModal(true);
        } else if (!response.data.outbound_transport_type && !response.data.transport_type) {
          setShowWelcomeModal(true);
        }
      }
    } catch (error) {
      console.error('Error fetching member info:', error);
      if (error.response?.status === 404) {
        const seenKey = `mytrip_explanation_${tripId}_${user?.id}`;
        const hasSeen = localStorage.getItem(seenKey);
        
        if (!hasSeen) {
          setShowExplanationModal(true);
        } else {
          setShowWelcomeModal(true);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [api, tripId, user?.id]);

  useEffect(() => {
    if (tripId) {
      fetchMemberInfo();
    }
  }, [tripId, fetchMemberInfo]);

  // Update member info
  const updateMemberInfo = async (data) => {
    try {
      setSaving(true);
      const response = await api.put(`/trips/${tripId}/member-info/me`, data);
      setMemberInfo(response.data);
      toast.success(t('myTrip.savedSuccess'));
      return response.data;
    } catch (error) {
      console.error('Error updating member info:', error);
      toast.error(t('myTrip.savedError'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Handle explanation modal close
  const handleExplanationClose = () => {
    const seenKey = `mytrip_explanation_${tripId}_${user?.id}`;
    localStorage.setItem(seenKey, 'true');
    setShowExplanationModal(false);
  };

  // Start filling after explanation
  const handleStartFilling = () => {
    handleExplanationClose();
    setShowWelcomeModal(true);
  };

  // Save welcome modal data
  const handleWelcomeSave = async (data) => {
    const result = await updateMemberInfo(data);
    setShowWelcomeModal(false);
    
    // If transport was set and it's not shared transport, ask about costs
    if (result && data.outbound_transport_type && !isSharedTransport) {
      await delay(300);
      setPendingTransportCostDirection('outbound');
      setShowTransportCostModal(true);
    }
  };

  // Skip welcome modal
  const handleWelcomeLater = () => {
    setShowWelcomeModal(false);
  };

  // Delete existing transport payments
  const deleteTransportPayments = async (direction) => {
    try {
      const response = await api.get(`/trips/${tripId}/payments`);
      const payments = response.data || [];
      
      const transportPayments = payments.filter(p => 
        p.category === 'transport' && 
        p.related_to === `transport_${direction}` &&
        p.related_member_id === user?.id
      );
      
      for (const payment of transportPayments) {
        await api.delete(`/trips/${tripId}/payments/${payment.id}`);
      }
    } catch (error) {
      console.error('Error deleting transport payments:', error);
    }
  };

  // Save transport cost and create payment
  const handleTransportCostSave = async (costData) => {
    const direction = pendingTransportCostDirection;
    const transportType = direction === 'outbound' 
      ? memberInfo?.outbound_transport_type 
      : memberInfo?.return_transport_type;
    
    try {
      const directionLabel = direction === 'outbound' ? t('myTrip.transport.outbound') : t('myTrip.transport.return');
      const transportLabel = TRANSPORT_TYPES[transportType]?.label[language] || transportType;
      
      const paymentData = {
        title: `${directionLabel} - ${transportLabel}`,
        total_amount: costData.amount,
        category: 'transport',
        payment_type: 'selective',
        selected_member_ids: [user?.id],
        related_to: `transport_${direction}`,
        related_member_id: user?.id
      };

      if (costData.installments === 1) {
        await api.post(`/trips/${tripId}/payments`, {
          ...paymentData,
          is_paid: costData.isPaid
        });
      } else {
        const installmentAmount = costData.amount / costData.installments;
        for (let i = 1; i <= costData.installments; i++) {
          await api.post(`/trips/${tripId}/payments`, {
            ...paymentData,
            title: `${paymentData.title} (${i}/${costData.installments})`,
            total_amount: installmentAmount
          });
        }
      }

      toast.success(t('myTrip.transportCost.paymentCreated'));
      
      // If we just added outbound and return transport is set, ask about return
      if (direction === 'outbound' && memberInfo?.return_transport_type && !isSharedTransport) {
        await delay(300);
        setPendingTransportCostDirection('return');
        setShowTransportCostModal(true);
      }
    } catch (error) {
      console.error('Error creating transport payment:', error);
      toast.error(t('myTrip.transportCost.paymentError'));
    }
  };

  // Handle transport cost modal close
  const handleTransportCostClose = async (open) => {
    if (!open) {
      // If closing outbound cost modal and return transport exists, ask about return
      if (pendingTransportCostDirection === 'outbound' && memberInfo?.return_transport_type && !isSharedTransport) {
        setPendingTransportCostDirection('return');
        await delay(300);
        setShowTransportCostModal(true);
      } else {
        setPendingTransportCostDirection(null);
      }
    }
    setShowTransportCostModal(open);
  };

  // Handle edit transport click with warning
  const handleEditTransport = () => {
    // Check if there are existing transport payments
    setShowEditWarning(true);
  };

  // Confirm edit after warning
  const handleConfirmEdit = async () => {
    setShowEditWarning(false);
    // Delete existing transport payments
    await deleteTransportPayments('outbound');
    await deleteTransportPayments('return');
    setShowWelcomeModal(true);
  };

  // Save transport details
  const handleTransportDetailsSave = async (details) => {
    const direction = editingTransportDirection || 'outbound';
    const detailsKey = direction === 'outbound' 
      ? `outbound_${memberInfo?.outbound_transport_type}_details`
      : `return_${memberInfo?.return_transport_type}_details`;
    
    await updateMemberInfo({
      [detailsKey]: details
    });
    setEditingTransportDirection(null);
  };

  // Toggle accommodation
  const handleAccommodationToggle = async (checked) => {
    await updateMemberInfo({ has_different_accommodation: checked });
    if (checked && !memberInfo?.accommodation_details?.name) {
      setShowAccommodationModal(true);
    }
  };

  // Save accommodation
  const handleAccommodationSave = async (details) => {
    await updateMemberInfo({ 
      has_different_accommodation: true,
      accommodation_details: details 
    });
  };

  // Add dependent
  const handleAddDependent = async (data) => {
    try {
      setSaving(true);
      const response = await api.post(`/trips/${tripId}/member-info/me/dependents`, data);
      setMemberInfo(response.data);
      toast.success(t('myTrip.dependent.addedSuccess'));
    } catch (error) {
      console.error('Error adding dependent:', error);
      toast.error(t('myTrip.dependent.addedError'));
    } finally {
      setSaving(false);
    }
  };

  // Update dependent
  const handleUpdateDependent = async (data) => {
    if (!editingDependent) return;
    try {
      setSaving(true);
      const response = await api.put(
        `/trips/${tripId}/member-info/me/dependents/${editingDependent.id}`,
        data
      );
      setMemberInfo(response.data);
      setEditingDependent(null);
      toast.success(t('myTrip.dependent.updatedSuccess'));
    } catch (error) {
      console.error('Error updating dependent:', error);
      toast.error(t('myTrip.dependent.updatedError'));
    } finally {
      setSaving(false);
    }
  };

  // Delete dependent
  const handleDeleteDependent = async (dependentId) => {
    if (!window.confirm(t('myTrip.dependent.deleteConfirm'))) return;
    try {
      setSaving(true);
      const response = await api.delete(
        `/trips/${tripId}/member-info/me/dependents/${dependentId}`
      );
      setMemberInfo(response.data);
      toast.success(t('myTrip.dependent.deletedSuccess'));
    } catch (error) {
      console.error('Error deleting dependent:', error);
      toast.error(t('myTrip.dependent.deletedError'));
    } finally {
      setSaving(false);
    }
  };

  // Mark as complete
  const handleMarkComplete = async () => {
    await updateMemberInfo({ is_completed: true });
  };

  // Get transport details by direction
  const getTransportDetails = (direction) => {
    const transportType = direction === 'outbound' 
      ? memberInfo?.outbound_transport_type 
      : memberInfo?.return_transport_type;
    
    if (!transportType) return null;
    
    const key = `${direction}_${transportType}_details`;
    return memberInfo?.[key] || memberInfo?.[`${transportType}_details`];
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  // Organizer not traveling
  if (isOrganizer && memberInfo?.is_traveling === false) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="py-8 text-center">
            <Crown className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('myTrip.organizerNotTraveling.title')}</h3>
            <p className="text-slate-500 mb-4">{t('myTrip.organizerNotTraveling.description')}</p>
            <Button 
              variant="outline" 
              onClick={() => updateMemberInfo({ is_traveling: true })}
            >
              {t('myTrip.organizerNotTraveling.changeAnswer')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const outboundTransportConfig = effectiveTransport.outbound ? TRANSPORT_TYPES[effectiveTransport.outbound] : null;
  const returnTransportConfig = effectiveTransport.return ? TRANSPORT_TYPES[effectiveTransport.return] : null;
  const OutboundIcon = outboundTransportConfig ? iconMap[outboundTransportConfig.icon] : null;
  const ReturnIcon = returnTransportConfig ? iconMap[returnTransportConfig.icon] : null;

  // Transport card component
  const TransportCard = ({ direction, transportConfig, Icon, details }) => {
    const isOutbound = direction === 'outbound';
    const label = isOutbound ? t('myTrip.transport.outbound') : t('myTrip.transport.return');
    const bgColor = isOutbound ? 'orange' : 'blue';
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isOutbound ? (
              <ArrowRight className={`w-4 h-4 text-${bgColor}-500`} />
            ) : (
              <ArrowLeft className={`w-4 h-4 text-${bgColor}-500`} />
            )}
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">{label}</span>
          </div>
        </div>
        
        {transportConfig ? (
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-${bgColor}-100 dark:bg-${bgColor}-900/30`}>
              {Icon && <Icon className={`w-5 h-5 text-${bgColor}-600`} />}
            </div>
            <span className="font-medium">
              {transportConfig.label[language] || transportConfig.label['pt-BR']}
            </span>
          </div>
        ) : (
          <p className="text-sm text-slate-500">{t('myTrip.transport.notSet')}</p>
        )}
        
        {transportConfig && (
          <div 
            className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            onClick={() => {
              setEditingTransportDirection(direction);
              setShowTransportModal(true);
            }}
          >
            {details && Object.keys(details).some(k => details[k]) ? (
              <div className="space-y-1 text-sm">
                {details.airline && <p className="truncate"><span className="text-slate-500">{t('myTrip.transport.airline')}:</span> {details.airline}</p>}
                {details.flight_number && <p><span className="text-slate-500">{t('myTrip.transport.flightNumber')}:</span> {details.flight_number}</p>}
                {details.company && <p className="truncate"><span className="text-slate-500">{t('myTrip.transport.company')}:</span> {details.company}</p>}
                {details.car_model && <p><span className="text-slate-500">{t('myTrip.transport.carModel')}:</span> {details.car_model}</p>}
                {details.departure_datetime && (
                  <p><span className="text-slate-500">{t('myTrip.transport.departure')}:</span> {format(new Date(details.departure_datetime), 'dd/MM HH:mm', { locale })}</p>
                )}
                <p className={`text-${bgColor}-500 text-xs mt-1`}>{t('myTrip.clickToEdit')}</p>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-sm">{t('myTrip.transport.addDetails')}</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <ScrollArea className="h-[calc(100vh-180px)]">
      <div className="space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{t('myTrip.title')}</h1>
            <p className="text-sm text-slate-500">{t('myTrip.subtitle')}</p>
          </div>
          {memberInfo?.is_completed ? (
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 text-xs">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {t('myTrip.completed')}
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs">
              <Clock className="w-3 h-3 mr-1" />
              {t('myTrip.incomplete')}
            </Badge>
          )}
        </div>

        {/* Trip route (read-only) */}
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500">{t('myTrip.origin')}</p>
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="font-medium text-sm truncate">{trip?.origin || '-'}</span>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500">{t('myTrip.destination')}</p>
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-orange-500 flex-shrink-0" />
                  <span className="font-medium text-sm truncate">{trip?.destination || '-'}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Shared Transport Alert */}
        {isSharedTransport && (
          <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <Crown className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-sm text-blue-700 dark:text-blue-300">
              {t('myTrip.sharedTransportAlert') || 'O organizador já definiu o transporte para todos os membros. Os dados abaixo não podem ser editados.'}
            </AlertDescription>
          </Alert>
        )}

        {/* Transport - Ida e Volta */}
        <Card>
          <CardHeader className="pb-2 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Plane className="w-5 h-5 text-orange-500" />
                {t('myTrip.transport.title')}
              </CardTitle>
              {!isSharedTransport && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleEditTransport}
                  className="h-8 text-xs"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  {t('common.edit')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-4 space-y-4">
            {(outboundTransportConfig || returnTransportConfig) ? (
              <>
                <TransportCard 
                  direction="outbound"
                  transportConfig={outboundTransportConfig}
                  Icon={OutboundIcon}
                  details={getTransportDetails('outbound')}
                />
                <div className="border-t border-slate-100 dark:border-slate-800" />
                <TransportCard 
                  direction="return"
                  transportConfig={returnTransportConfig}
                  Icon={ReturnIcon}
                  details={getTransportDetails('return')}
                />
              </>
            ) : (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setShowWelcomeModal(true)}
              >
                {t('myTrip.transport.select')}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Attending days */}
        <Card>
          <CardHeader className="pb-2 px-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              {t('myTrip.days.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4">
            {memberInfo?.attending_days?.length > 0 ? (
              <div className="space-y-2">
                <div className="overflow-x-auto -mx-4 px-4">
                  <div className="flex gap-2 pb-1" style={{ minWidth: 'max-content' }}>
                    {memberInfo.attending_days.map((dateStr) => {
                      const date = parseISO(dateStr);
                      return (
                        <Badge 
                          key={dateStr} 
                          variant="secondary" 
                          className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 whitespace-nowrap"
                        >
                          {format(date, 'EEE dd/MM', { locale })}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowWelcomeModal(true)}
                  className="text-slate-500 h-7 text-xs"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  {t('common.edit')}
                </Button>
              </div>
            ) : (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setShowWelcomeModal(true)}
              >
                {t('myTrip.days.select')}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Accommodation */}
        <Card>
          <CardHeader className="pb-2 px-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-5 h-5 text-orange-500" />
              {t('myTrip.accommodation.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <Label className="text-sm">{t('myTrip.accommodation.hasDifferent')}</Label>
                <p className="text-xs text-slate-500 truncate">{t('myTrip.accommodation.hasDifferentDesc')}</p>
              </div>
              <Switch
                checked={memberInfo?.has_different_accommodation || false}
                onCheckedChange={handleAccommodationToggle}
                disabled={saving}
                className="flex-shrink-0 ml-2"
              />
            </div>

            {memberInfo?.has_different_accommodation && (
              <div 
                className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                onClick={() => setShowAccommodationModal(true)}
              >
                {memberInfo?.accommodation_details?.name ? (
                  <div className="space-y-1 text-sm">
                    <p className="font-medium truncate">{memberInfo.accommodation_details.name}</p>
                    {memberInfo.accommodation_details.address && (
                      <p className="text-slate-500 text-xs truncate">{memberInfo.accommodation_details.address}</p>
                    )}
                    {memberInfo.accommodation_details.check_in && (
                      <p className="text-xs"><span className="text-slate-500">Check-in:</span> {format(new Date(memberInfo.accommodation_details.check_in), 'dd/MM HH:mm', { locale })}</p>
                    )}
                    <p className="text-orange-500 text-xs mt-1">{t('myTrip.clickToEdit')}</p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500 text-sm">{t('myTrip.accommodation.addDetails')}</span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dependents */}
        <Card>
          <CardHeader className="pb-2 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" />
                {t('myTrip.dependent.title')}
              </CardTitle>
              <Button 
                size="sm" 
                onClick={() => {
                  setEditingDependent(null);
                  setShowDependentModal(true);
                }}
                disabled={saving}
                className="h-8 text-xs"
              >
                <UserPlus className="w-3 h-3 mr-1" />
                {t('myTrip.dependent.add')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-4">
            {memberInfo?.dependent_members?.length > 0 ? (
              <div className="space-y-2">
                {memberInfo.dependent_members.map((dep) => {
                  const statusConfig = DEPENDENT_STATUS[dep.status] || DEPENDENT_STATUS.pending;
                  const relationshipLabel = RELATIONSHIP_OPTIONS.find(r => r.value === dep.relationship);
                  
                  return (
                    <div 
                      key={dep.id} 
                      className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-sm flex-shrink-0">
                          👤
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{dep.name}</p>
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            {dep.age && <span>{dep.age} {t('myTrip.dependent.years')}</span>}
                            {relationshipLabel && (
                              <span className="truncate">• {relationshipLabel.label[language] || relationshipLabel.label['pt-BR']}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Badge className={`${statusConfig.bgClass} ${statusConfig.textClass} text-[10px]`}>
                          {statusConfig.label[language] || statusConfig.label['pt-BR']}
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            setEditingDependent(dep);
                            setShowDependentModal(true);
                          }}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-7 w-7 text-red-500 hover:text-red-600"
                          onClick={() => handleDeleteDependent(dep.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-3">
                {t('myTrip.dependent.empty')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Complete button */}
        {!memberInfo?.is_completed && (
          <Button 
            className="w-full rounded-full bg-green-500 hover:bg-green-600"
            onClick={handleMarkComplete}
            disabled={saving || (!memberInfo?.outbound_transport_type && !memberInfo?.transport_type) || !memberInfo?.attending_days?.length}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            {t('myTrip.markComplete')}
          </Button>
        )}

        {/* Modals */}
        <WelcomeModal
          open={showWelcomeModal}
          onOpenChange={setShowWelcomeModal}
          trip={trip}
          isOrganizer={isOrganizer}
          memberInfo={memberInfo}
          onSave={handleWelcomeSave}
          onLater={handleWelcomeLater}
        />

        <TransportDetailsModal
          open={showTransportModal}
          onOpenChange={setShowTransportModal}
          transportType={editingTransportDirection === 'return' ? memberInfo?.return_transport_type : memberInfo?.outbound_transport_type}
          details={getTransportDetails(editingTransportDirection || 'outbound')}
          onSave={handleTransportDetailsSave}
        />

        <AccommodationModal
          open={showAccommodationModal}
          onOpenChange={setShowAccommodationModal}
          details={memberInfo?.accommodation_details}
          onSave={handleAccommodationSave}
        />

        <DependentModal
          open={showDependentModal}
          onOpenChange={setShowDependentModal}
          dependent={editingDependent}
          onSave={editingDependent ? handleUpdateDependent : handleAddDependent}
          isEditing={!!editingDependent}
        />

        <ExplanationModal
          open={showExplanationModal}
          onOpenChange={handleExplanationClose}
          onStartFilling={handleStartFilling}
        />

        <TransportCostModal
          open={showTransportCostModal}
          onOpenChange={handleTransportCostClose}
          tripId={tripId}
          transportType={pendingTransportCostDirection === 'return' ? memberInfo?.return_transport_type : memberInfo?.outbound_transport_type}
          direction={pendingTransportCostDirection}
          currency={tripCurrency}
          onSave={handleTransportCostSave}
        />

        {/* Edit Warning Dialog */}
        {showEditWarning && (
          <Dialog open={showEditWarning} onOpenChange={setShowEditWarning}>
            <DialogContent className="sm:max-w-md !p-0">
              <DialogHeader className="p-4 sm:p-6 pb-2">
                <DialogTitle className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="w-5 h-5" />
                  {t('myTrip.editWarning.title')}
                </DialogTitle>
              </DialogHeader>
              <div className="px-4 sm:px-6 pb-4">
                <Alert variant="warning" className="bg-amber-50 dark:bg-amber-900/20 border-amber-200">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <AlertDescription className="text-amber-700 dark:text-amber-300">
                    {t('myTrip.editWarning.message')}
                  </AlertDescription>
                </Alert>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 p-4 sm:p-6 pt-2 border-t">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full order-2 sm:order-1"
                  onClick={() => setShowEditWarning(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  className="flex-1 rounded-full bg-amber-500 hover:bg-amber-600 order-1 sm:order-2"
                  onClick={handleConfirmEdit}
                >
                  {t('myTrip.editWarning.confirm')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </ScrollArea>
  );
};

export default TripMyTrip;
